import React from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";

// Terms content component that can be used both in modal and as a page
export const TermsContent = () => {
    return (
        <div className="space-y-4 text-sm text-gray-600">
            
        <section className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-6">
          <h3 className="font-bold text-lg text-yellow-700">TRIAL VERSION NOTICE</h3>
          <p className="text-yellow-700">
            These Terms & Conditions are provisional and specifically apply to the SurfSnap trial period. 
            By participating in this trial, you acknowledge the experimental nature of the service and agree 
            that certain features may be limited or subject to change. A final version will be published 
            upon full launch of the platform.
          </p>
        </section>
            
        <section>
          <h3 className="font-medium text-lg">1. Introduction</h3>
          <p>
            Welcome to <strong>SurfSnap</strong> ("we," "us," or "our"). These Terms &amp; 
            Conditions ("Terms") govern your access to and use of the SurfSnap website, 
            mobile applications, and services (collectively, the "Platform"). By accessing 
            or using the Platform, you agree to be bound by these Terms. If you do not agree 
            with any part of these Terms, please do not use our Platform.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">2. Acceptance of Terms</h3>
          <p>
            By creating an account, purchasing photos, or otherwise using the Platform, 
            you acknowledge that you have read, understood, and agree to abide by these Terms, 
            as well as any additional guidelines, policies, or rules provided on the Platform.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">3. User Eligibility</h3>
          <p>
            You must be at least 18 years old (or the age of majority in your jurisdiction) 
            to create an account on SurfSnap. If you are under 18, you may only use the 
            Platform under the supervision of a parent or legal guardian who agrees to be 
            bound by these Terms.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">4. Photo Rights & Ownership</h3>
          <p>
            <strong>4.1 Photographers.</strong> Photographers retain ownership of any photos 
            they upload or sell via SurfSnap. By uploading photos, photographers grant 
            SurfSnap a worldwide, non-exclusive license to display and distribute the 
            photos solely for the purpose of promoting or facilitating transactions on 
            the Platform.
          </p>
          <p>
            <strong>4.2 Purchasers.</strong> When you purchase a photo, you receive a 
            non-exclusive, non-transferable license to use that photo for personal or 
            commercial purposes, unless otherwise stated. You may not redistribute, 
            resell, or sublicense the purchased photo without explicit permission 
            from the photographer.
          </p>
          <p>
            <strong>4.3 Watermarking.</strong> All photos displayed on the Platform are 
            protected with a SurfSnap watermark until purchased. Upon successful purchase, 
            you will receive access to the unwatermarked version of the photo. Attempting to 
            remove watermarks without purchase is strictly prohibited and may result in legal action.
          </p>
        </section>
        
        <section>
          <h3 className="font-medium text-lg">5. Photography Consent & Model Releases</h3>
          <p>
            <strong>5.1 Photographer Responsibilities.</strong> Photographers are solely responsible 
            for obtaining appropriate consent from identifiable individuals in their photos, especially 
            in beach settings where privacy expectations may exist. SurfSnap strongly recommends 
            that photographers obtain verbal consent before photographing identifiable individuals.
          </p>
          <p>
            <strong>5.2 Model Releases.</strong> For commercial use of photos featuring identifiable 
            individuals, purchasers are advised that they may need a model release. SurfSnap does not 
            provide model releases, and it is the photographer's responsibility to obtain and provide 
            them if required for commercial usage. Purchasers should contact the photographer directly 
            to inquire about model releases.
          </p>
          <p>
            <strong>5.3 Local Photography Laws.</strong> Photographers must comply with all local, 
            state, and federal laws regarding photography in public places, including beaches and 
            surf spots. SurfSnap does not assume responsibility for ensuring such compliance, and 
            photographers indemnify SurfSnap against any claims arising from non-compliance with 
            applicable laws.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">6. Commission and Fees</h3>
          <p>
            By using SurfSnap, you agree to the following fee structure:
          </p>
          <ul className="list-disc pl-4">
            <li>
              SurfSnap retains <strong>15%</strong> of each photo sale as a platform fee.
            </li>
            <li>
              Photographers receive <strong>85%</strong> of the sale price.
            </li>
            <li>
              Payment processing fees are included in the platform fee.
            </li>
            <li>
              Payouts to photographers are processed through our secure payment system.
            </li>
          </ul>
        </section>

        <section>
          <h3 className="font-medium text-lg">7. Payment Terms</h3>
          <p>
            <strong>7.1 Payment Processing.</strong> All payments for photo purchases 
            are processed through our secure third-party payment processors (Stripe and PayPal). By 
            making a purchase, you agree to the respective processor's terms and 
            conditions, in addition to these Terms.
          </p>
          <p>
            <strong>7.2 Refunds and Chargebacks.</strong> All purchases made through SurfSnap are final. 
            We do not offer refunds for any transaction. You acknowledge that, in the event of a chargeback, 
            any funds previously credited to a photographer may be reversed. By completing a purchase, you agree 
            to this no-refund policy.
          </p>
          <p>
            <strong>7.3 Payout Schedules.</strong> Payouts to photographers are made 
            according to our internal payout schedule. We reserve the right to withhold 
            payouts if we suspect fraudulent activity or violation of these Terms.
          </p>
          <p>
            <strong>7.4 Taxes.</strong> You are responsible for any applicable taxes 
            relating to your use of the Platform, including sales tax, value-added tax 
            (VAT), or income tax on any earnings.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">8. Content Guidelines</h3>
          <p>
            You may not upload content that is illegal, offensive, or infringes any 
            third-party rights. SurfSnap reserves the right to remove or disable access 
            to any content that violates these guidelines, without prior notice and at 
            our sole discretion.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">9. Copyright Infringement & DMCA</h3>
          <p>
            SurfSnap respects the intellectual property rights of others. If you believe 
            that your work has been copied in a way that constitutes copyright infringement, 
            please submit a notification containing the following information:
          </p>
          <ul className="list-disc pl-4">
            <li>An electronic or physical signature of the person authorized to act on behalf of the copyright owner</li>
            <li>Identification of the copyrighted work claimed to have been infringed</li>
            <li>Identification of the material that is claimed to be infringing</li>
            <li>Your contact information, including address, telephone number, and email</li>
            <li>A statement that you have a good faith belief that the disputed use is not authorized</li>
            <li>A statement, under penalty of perjury, that the information in your notification is accurate</li>
          </ul>
          <p>
            Send DMCA notifications to: <a href="mailto:copyright@surfsnap.com" className="text-blue-600">copyright@surfsnap.com</a>
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">10. Liability & Disclaimers</h3>
          <p>
            <strong>10.1 "As Is" Basis.</strong> The Platform is provided on an "as is" 
            and "as available" basis. We do not guarantee that the Platform will be 
            uninterrupted or error-free.
          </p>
          <p>
            <strong>10.2 Limitation of Liability.</strong> To the fullest extent permitted 
            by law, SurfSnap shall not be liable for any indirect, incidental, 
            consequential, or special damages, including loss of profits or data, 
            arising from or related to your use of the Platform.
          </p>
          <p>
            <strong>10.3 Use of Photos.</strong> SurfSnap is not liable for any misuse 
            of photos by purchasers or other third parties. It is your responsibility 
            to enforce and protect your rights to your content.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">11. Account Suspension & Termination</h3>
          <p>
            We reserve the right to suspend or terminate your account at our sole 
            discretion if we believe you have violated these Terms or engaged in 
            fraudulent or illegal activity. In such cases, we may also revoke your 
            access to the Platform without notice or liability.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">12. Privacy Policy</h3>
          <p>
            Your use of the Platform is also governed by our 
            <a href="/privacy-policy" className="text-blue-600"> Privacy Policy</a>, 
            which explains how we collect, use, and share your personal information. 
            By using the Platform, you agree to the terms outlined in our Privacy Policy.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">13. Dispute Resolution</h3>
          <p>
            <strong>13.1 Informal Resolution.</strong> Before initiating any formal legal 
            proceeding, you agree to first contact us directly to seek an informal resolution 
            to any dispute arising from or relating to the Platform.
          </p>
          <p>
            <strong>13.2 Arbitration.</strong> Any disputes that cannot be resolved informally 
            shall be resolved through binding arbitration, in accordance with the rules of the 
            jurisdiction in which SurfSnap operates. You agree to waive your right to a jury trial 
            or to participate in a class action.
          </p>
          <p>
            <strong>13.3 Photographer-Customer Disputes.</strong> In the event of a dispute between 
            a photographer and a customer regarding a photo, SurfSnap may, at its discretion, act as 
            an intermediary but is not obligated to resolve such disputes. We encourage users to 
            communicate directly with each other to resolve any issues.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">14. Changes to Terms</h3>
          <p>
            We may update these Terms at any time. We will notify users of any material 
            changes by posting the updated Terms on the Platform or through other 
            communication channels. Your continued use of the Platform after any changes 
            indicates your acceptance of the updated Terms.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">15. Contact Information</h3>
          <p>
            If you have any questions or concerns regarding these Terms &amp; Conditions, 
            please contact us at 
            <a href="mailto:support@surfsnap.com" className="text-blue-600">
              {" "}support@surfsnap.com
            </a>.
          </p>
        </section>

        <section>
          <h3 className="font-medium text-lg">16. Miscellaneous</h3>
          <p>
            If any provision of these Terms is found to be invalid or unenforceable, 
            the remaining provisions shall remain in full force and effect. These Terms 
            constitute the entire agreement between you and SurfSnap regarding your use 
            of the Platform, superseding any prior agreements.
          </p>
        </section>

      </div>
    );
};